/**
 * Recently Viewed — DOM-order enforced (newest → oldest)
 * Relies on localStorage['dc-recently-viewed'] created elsewhere (tracker).
 * Uses Search & Discovery HTML, then reorders tiles to match stored IDs/handles.
 */

if (!customElements.get('recently-viewed-products')) {
  // ---------- helpers ----------
  function getKeyFromTile(el) {
    // Try ID then handle, then derive from product link
    const byDataId =
      el.dataset.productId || el.getAttribute('data-product-id');
    const byDataHandle =
      el.dataset.productHandle || el.getAttribute('data-product-handle');

    if (byDataId) return String(byDataId);
    if (byDataHandle) return String(byDataHandle);

    // Fallback: parse handle from first product link found
    const href =
      el.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    if (!href) return null;
    const after = href.split('/products/')[1] || '';
    if (!after) return null;
    const handle = after.split('?')[0].replace(/\/$/, '');
    return handle ? String(handle) : null;
  }

  function reorderByRecent(container, orderedKeys) {
    const items = Array.from(container.children);
    const map = new Map();

    items.forEach(el => {
      const key = getKeyFromTile(el);
      if (key) map.set(key, el);
    });

    // rebuild in our desired order
    container.innerHTML = '';
    orderedKeys.forEach(k => {
      const el = map.get(String(k));
      if (el) container.appendChild(el);
    });

    // append any leftovers (S&D might include extras)
    items.forEach(el => {
      if (!container.contains(el)) container.appendChild(el);
    });
  }

  // ---------- web component ----------
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();

      // base search route
      this.baseUrl = (window.theme?.routes?.search_url) || '/search';
      this.url = this.baseUrl + '?view=recently-viewed&type=product&q=';

      // DOM refs
      this.container = this.querySelector('.products');
      this.carousel = this.querySelectorAll('.carousel');
      this.previous = this.querySelector('.flickity-prev');
      this.next = this.querySelector('.flickity-next');

      // current product (prefer ID)
      this.currentId = this.dataset.productId || null;
      this.currentHandle = this.dataset.productHandle || null;

      // config
      this.max = 8;

      // storage
      this.STORAGE_KEY = 'dc-recently-viewed';
      this.recentlyViewedIds = [];
      try {
        const raw = localStorage.getItem(this.STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) this.recentlyViewedIds = parsed;
        }
      } catch (_) {
        this.recentlyViewedIds = [];
      }

      // we’ll keep a copy of the ordered keys we intend to show (newest → oldest)
      this.orderedIds = [];
    }

    connectedCallback() {
      this.buildUrl();
      this.handleWindowResize(); // iOS Safari nudge
    }

    // Build query using newest-first from storage; skip current; cap to max.
    buildUrl() {
      // Newest-first: a lot of trackers append; safest is to reverse here.
      const stored = (this.recentlyViewedIds || []).slice().reverse();

      // Skip current product by ID if we have it; otherwise leave it (S&D will usually filter anyway)
      const want = [];
      for (const val of stored) {
        if (this.currentId && String(val) === String(this.currentId)) continue;
        if (want.length >= this.max) break;
        want.push(val);
      }

      if (!want.length) return;
      this.orderedIds = want.map(String); // keep as strings for key matching

      // Build S&D query. It supports id: filters; handles may still work via template logic.
      const parts = this.orderedIds.map(v => (/^\d+$/.test(v) ? `id:${v}` : `id:${v}`));
      const query = parts.join(' OR ');
      this.url = this.baseUrl + '?view=recently-viewed&type=product&q=' + encodeURIComponent(query);

      this.fetchProducts();
    }

    fetchProducts() {
      fetch(this.url)
        .then(r => r.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;

          // Your recently-viewed template should render a .products wrapper
          const list = html.querySelector('.products');

          if (list && list.innerHTML.trim()) {
            // inject HTML
            this.container.innerHTML = list.innerHTML;
            this.classList.add('product-recommendations--loaded');
            const emptyDiv = document.querySelector('.empty-prodcuts-message');
            if (emptyDiv) emptyDiv.style.display = 'none';

            // *** enforce newest→oldest visual order ***
            reorderByRecent(this.container, this.orderedIds);
          }

          if (this.carousel && this.carousel.length) {
            this.initSlider();
            setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
          }
        })
        .catch(e => {
          console.error('[Recently Viewed] fetchProducts error:', e);
        });
    }

    initSlider() {
      const args = {
        wrapAround: false,
        cellAlign: 'left',
        pageDots: false,
        contain: true,
        autoPlay: false,
        prevNextButtons: false,
        draggable: true,
        cellSelector: '.carousel__slide',
        adaptiveHeight: true,
      };

      this.carousel.forEach(slider => {
        const flkty = new Flickity(slider, args);

        if (this.previous) {
          this.previous.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            flkty.previous();
          });
        }

        if (this.next) {
          this.next.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            flkty.next();
          });
        }
      });
    }

    handleWindowResize() {
      const accordion = document.querySelector('.cart-recently-viewed-wrapper summary');
      if (accordion) {
        accordion.addEventListener('click', () => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
